import React, { useState, useEffect, useCallback } from 'react';
import { MdSmartDisplay, MdPlayArrow, MdOutlineFileDownload } from 'react-icons/md';
import { HiArrowUpTray } from 'react-icons/hi2';
import { getActiveAccount } from './utils/accountUtils';
import { API_BASE_URL, normalizeMediaUrl } from './config';
import VideoCard from './VideoCard';
import './YourVideosPage.css';
import './VideoGrid.css';

const DEFAULT_FALLBACK_THUMBNAIL = normalizeMediaUrl('/drive/video_thumbnails/thumbnail-157.jpeg') || `${process.env.PUBLIC_URL || ''}/img/banner.png`;

function formatDuration(sec) {
  if (!sec && sec !== 0) return '0:00';
  if (typeof sec === 'string' && sec.includes(':')) return sec;
  const num = parseInt(sec, 10);
  if (isNaN(num)) return '0:00';
  const m = Math.floor(num / 60);
  const s = num % 60;
  return `${m}:${s < 10 ? '0' : ''}${s}`;
}

function timeAgo(dateString) {
  if (!dateString) return 'Just now';
  const diff = (Date.now() - new Date(dateString).getTime()) / 1000;
  if (diff < 60) return 'Just now';
  if (diff < 3600) {
    const mins = Math.floor(diff / 60);
    return `${mins} minute${mins === 1 ? '' : 's'} ago`;
  }
  if (diff < 86400) {
    const hrs = Math.floor(diff / 3600);
    return `${hrs} hour${hrs === 1 ? '' : 's'} ago`;
  }
  if (diff < 2592000) {
    const days = Math.floor(diff / 86400);
    return `${days} day${days === 1 ? '' : 's'} ago`;
  }
  const months = Math.floor(diff / 2592000);
  return `${months} month${months === 1 ? '' : 's'} ago`;
}

export default function YourVideosPage({ userProfile, onNavigate, onOpenUpload }) {
  const [activeTab, setActiveTab] = useState('videos');
  const [videos, setVideos] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const currentAcc = userProfile || getActiveAccount();
  const myHandle = currentAcc?.handle || (currentAcc?.name ? `@${currentAcc.name.toLowerCase().replace(/[^a-z0-9]/gi, '')}` : '@user');

  const fetchMyVideos = useCallback(async () => {
    setIsLoading(true);
    let backendVideos = [];

    // 1. Fetch channel videos from backend
    try {
      const res = await fetch(`${API_BASE_URL}/api/v1/videos/channels/${encodeURIComponent(myHandle)}?limit=50`, {
        headers: {
          token: 'c7e993808a344da9d59cfa20fa8b6061',
          devicetype: '0',
          request_type: 'USER',
        },
      });

      if (res.ok) {
        const json = await res.json();
        const raw = json?.data?.videos || [];
        backendVideos = raw.map((v) => ({
          id: String(v.id || v.video_id),
          videoUrlId: v.video_url_id || 'HyRyzOCHONj',
          title: v.title || 'Untitled Video',
          description: v.description || '',
          views: v.views || 0,
          duration: v.duration,
          thumbnailUrl: normalizeMediaUrl(v.thumbnail_url) || DEFAULT_FALLBACK_THUMBNAIL,
          createdDate: v.created_date || new Date().toISOString(),
          channelName: v.channel_name || currentAcc?.name || 'Creator',
        }));
      }
    } catch (err) {
      console.warn('Failed to fetch user channel videos:', err);
    }

    // 2. Merge with locally queued uploads for this user handle
    try {
      const storageKey = `speedotube_channel_videos_${myHandle.toLowerCase()}`;
      const savedLocal = localStorage.getItem(storageKey);
      if (savedLocal) {
        const localList = JSON.parse(savedLocal);
        const backendIds = new Set(backendVideos.map((v) => String(v.id)));
        localList.forEach((lv) => {
          const lvId = String(lv.dbId || lv.id || lv.videoUrlId);
          if (!backendIds.has(lvId) && !['vid_101', 'vid_102', 'vid_103', 'vid_demo'].includes(lv.id)) {
            backendVideos.unshift({
              id: lvId,
              videoUrlId: lv.videoUrlId || 'HyRyzOCHONj',
              title: lv.title || 'Uploaded Video',
              description: lv.description || '',
              views: lv.views || 0,
              duration: lv.duration || 173,
              thumbnailUrl: lv.thumbnailUrl || DEFAULT_FALLBACK_THUMBNAIL,
              createdDate: lv.createdAt || new Date().toISOString(),
              channelName: currentAcc?.name || 'Creator',
            });
          }
        });
      }
    } catch (e) {}

    setVideos(backendVideos);
    setIsLoading(false);
  }, [myHandle, currentAcc]);

  useEffect(() => {
    fetchMyVideos();

    const handleUpdated = () => fetchMyVideos();
    window.addEventListener('speedotube_video_published', handleUpdated);
    window.addEventListener('speedotube_album_published', handleUpdated);
    window.addEventListener('speedotube_user_switched', handleUpdated);
    window.addEventListener('speedotube_channel_updated', handleUpdated);
    return () => {
      window.removeEventListener('speedotube_video_published', handleUpdated);
      window.removeEventListener('speedotube_album_published', handleUpdated);
      window.removeEventListener('speedotube_user_switched', handleUpdated);
      window.removeEventListener('speedotube_channel_updated', handleUpdated);
    };
  }, [fetchMyVideos]);

  const handleTriggerUpload = () => {
    if (onOpenUpload) {
      onOpenUpload();
    } else if (onNavigate) {
      onNavigate('/studio/upload-video');
    }
  };

  const handlePlayVideo = (videoUrlId) => {
    if (onNavigate) {
      onNavigate(`/watch?v=${videoUrlId}`);
    }
  };

  const renderVideoCard = (vid) => {
    const preparedVideo = {
      ...vid,
      id: String(vid.id || vid.videoUrlId),
      videoUrlId: vid.videoUrlId || String(vid.id),
      title: vid.title || 'Untitled Video',
      channelName: vid.channelName || currentAcc?.name || 'Speedo Creator',
      handleName: vid.handleName || myHandle || '',
      channelAvatar: vid.channelAvatar || currentAcc?.avatar_url || currentAcc?.profile_image || '',
      verified: true,
      views: vid.views !== undefined ? `${vid.views}` : '0',
      timestamp: timeAgo(vid.createdDate || vid.createdAt),
      duration: formatDuration(vid.duration),
      rawDuration: typeof vid.duration === 'number' ? vid.duration : parseInt(vid.duration, 10) || 0,
      thumbnailUrl: vid.thumbnailUrl || DEFAULT_FALLBACK_THUMBNAIL,
    };

    return (
      <VideoCard
        key={preparedVideo.id}
        video={preparedVideo}
        onVideoClick={(v) => {
          handlePlayVideo(v.videoUrlId || v.id);
        }}
        onChannelClick={() => {
          if (onNavigate) {
            onNavigate('/profile');
          } else {
            window.history.pushState({}, '', '/profile');
            window.dispatchEvent(
              new CustomEvent('speedotube_navigate', { detail: { path: '/profile' } })
            );
          }
        }}
      />
    );
  };

  return (
    <div className="yv-page-container">
      {/* Page Title */}
      <div className="yv-header">
        <h1 className="yv-main-title">Your videos</h1>
      </div>

      {/* Navigation Tabs Bar */}
      <div className="yv-tabs-bar">
        <button
          className={`yv-tab-btn ${activeTab === 'videos' ? 'active' : ''}`}
          onClick={() => setActiveTab('videos')}
        >
          Videos
        </button>
        <button
          className={`yv-tab-btn ${activeTab === 'about' ? 'active' : ''}`}
          onClick={() => setActiveTab('about')}
        >
          About
        </button>
      </div>

      {/* Tab: Videos Content */}
      {activeTab === 'videos' && (
        <div className="yv-content-area">
          {isLoading ? (
            <div className="yv-loading">Loading your videos...</div>
          ) : videos.length === 0 ? (
            /* Exact Empty State matching user's design image */
            <div className="yv-empty-card">
              <div className="yv-empty-play-badge">
                <svg viewBox="0 0 24 24" width="46" height="46" fill="currentColor">
                  <path d="M19 4H5c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm-9 11V9l6 3-6 3z" />
                </svg>
              </div>

              <h2 className="yv-empty-title">This channel has no videos yet</h2>
              <p className="yv-empty-subtitle">
                Upload your first video to start growing your channel!
              </p>

              <button
                className="yv-upload-primary-btn"
                onClick={handleTriggerUpload}
              >
                <HiArrowUpTray size={17} className="yv-upload-icon" />
                <span>Upload a video</span>
              </button>
            </div>
          ) : (
            /* Uploaded Videos Grid (Exact 3 Videos Per Row layout as Home Page) */
            <div className="yv-videos-grid yt-video-grid">
              {videos.map(renderVideoCard)}
            </div>
          )}
        </div>
      )}


      {/* Tab: About Content */}
      {activeTab === 'about' && (
        <div className="yv-content-area">
          <div className="yv-about-card">
            <h3 className="yv-about-header">Channel Details</h3>
            <div className="yv-about-row">
              <span className="yv-about-label">Channel Name:</span>
              <span className="yv-about-value">{currentAcc?.name || 'Speedo Creator'}</span>
            </div>
            <div className="yv-about-row">
              <span className="yv-about-label">Handle:</span>
              <span className="yv-about-value">{myHandle}</span>
            </div>
            <div className="yv-about-row">
              <span className="yv-about-label">Total Videos:</span>
              <span className="yv-about-value">{videos.length}</span>
            </div>
            <div className="yv-about-row">
              <span className="yv-about-label">Status:</span>
              <span className="yv-about-value active">Active Channel</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
